import { create } from "zustand";
import type { Horizon, Mode, Timeframe } from "../lib/rounds";
import { storage as appStorage, type TypedStorage } from "../lib/storage";
import type { Preset } from "../types";

/**
 * Round history (GRA-123): every completed Round, Predict and Trade alike,
 * appended to one persisted, newest-first log. This is the seam the
 * reason-note and session-summary tickets build on, so the entry shape is
 * generic across every Loop and the store's own API is one call:
 * `appendHistory`. Persistence follows the same approach as the Loop stores'
 * score/Streak (lib/storage), and the log is capped so it cannot grow the
 * storage payload without bound over a long session.
 */

const HISTORY_KEY = "history:entries";

/** How many rounds the history keeps; older entries fall off the end. */
export const HISTORY_CAP = 200;

export type HistoryOutcome = "correct" | "incorrect" | "win" | "loss" | "flat";

/**
 * Which kind of run a Round was played in (GRA-154). Free play and a recorded
 * Daily challenge attempt both count towards the player's own numbers; a
 * practice replay of an already-recorded day does not, so the summary can
 * leave it out (see lib/sessionSummary.ts) and the list can say why.
 */
export type RunKind = "free" | "challenge" | "practice";

/** Player-facing names for the run kinds, used by the list and the summary. */
export const RUN_KIND_LABEL: Record<RunKind, string> = {
  free: "Free play",
  challenge: "Challenge",
  practice: "Practice",
};

/**
 * The trade a Trade-mode entry actually placed (GRA-154), as numbers rather
 * than as the formatted `choice` string, so a review can redraw the levels on
 * the chart and re-simulate the trade over the Round's own Forward.
 */
export type HistoryTrade = {
  side: "long" | "short";
  entry: number;
  stopLoss: number;
  takeProfit: number;
  risk: number;
};

/**
 * One completed Round, however it was played. `horizon` is Predict-only (the
 * guess kind, trend vs next-candle) and is null for Trade, which has no guess
 * horizon (CONTEXT.md "Guess"). `choice` and `detail` are pre-formatted,
 * player-facing strings so the history view can render every mode the same
 * way without knowing each Loop's own reveal shape.
 */
export type HistoryEntry = {
  id: string;
  /** `Date.now()` at the moment the Round was appended (reveal time). */
  timestamp: number;
  mode: Mode;
  timeframe: Timeframe;
  horizon: Horizon | null;
  instrumentId: string;
  instrumentName: string;
  dateRange: { start: string; end: string };
  /** The player's commitment, in player-facing words ("Higher", "Long"). */
  choice: string;
  outcome: HistoryOutcome;
  /** One-line resolution detail ("+2.3% · resolved by upper barrier in 4 candles"). */
  detail: string;
  /**
   * Realized dollar P&L (session-summary GRA-138), Trade-mode only. `detail`
   * already carries this formatted for display; this is the raw number so a
   * summary can sum it without reparsing player-facing text. Absent for
   * Predict entries, and for Trade entries logged before this field existed.
   */
  pnl?: number;
  /**
   * The player's own one-line reason for the decision (GRA-135), jotted down
   * before committing and carried through to reveal and to this entry.
   * Entirely optional: absent whenever the field was left blank, which costs
   * nothing elsewhere in the entry.
   */
  note?: string;
  /**
   * Which kind of run this Round was played in (GRA-154). Always present on a
   * new entry; entries written before this field existed are read back as
   * "free", which is what all but the challenge Loop wrote.
   */
  kind: RunKind;
  /**
   * The Round this entry was played on (GRA-154). What makes an entry
   * reviewable: without it there is no chart to reopen, which is exactly the
   * state of every entry written before this field existed. Optional forever,
   * therefore, rather than backfilled with a guess.
   */
  roundId?: string;
  /**
   * The difficulty preset the Round resolved at, for the Loops where a preset
   * decides the answer (the trend Loops and the Daily challenge). Absent for
   * Next-Candle and Trade, which read no preset.
   */
  preset?: Preset;
  /**
   * How many Context candles were charted, for the Loops that slice the
   * Context (trend and Trade). Absent where the whole stored Context is
   * charted (the Next-Candle Loops).
   */
  contextLength?: number;
  /** Trade-mode only: the levels the trade was placed at. */
  trade?: HistoryTrade;
};

/** What a Loop supplies; the store fills in `id` and `timestamp`. */
export type HistoryEntryInput = Omit<HistoryEntry, "id" | "timestamp">;

/** Whether this entry can reopen its own chart (GRA-154). */
export function canReview(entry: HistoryEntry): boolean {
  return typeof entry.roundId === "string" && entry.roundId.length > 0;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

const RUN_KINDS: RunKind[] = ["free", "challenge", "practice"];

/**
 * Read a persisted log back into the current entry shape (GRA-154). The one
 * field a stored entry can be missing and still be usable is `kind`, which
 * predates nothing: every entry written before run kinds existed was free
 * play or a challenge Round logged as free play, so "free" is the honest
 * default. Everything else new (`roundId`, `preset`, `contextLength`,
 * `trade`) stays absent, which is what makes an old entry show no Review.
 *
 * Anything that is not an object at all is dropped rather than repaired: a
 * corrupt log must not take the panel down with it.
 */
export function migrateHistoryEntries(raw: unknown): HistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: HistoryEntry[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const kind = RUN_KINDS.includes(item.kind as RunKind) ? (item.kind as RunKind) : "free";
    entries.push({ ...(item as unknown as HistoryEntry), kind });
  }
  return entries;
}

export interface HistoryState {
  /** Newest-first. */
  entries: HistoryEntry[];
  /** Append one completed Round, capping the log at `HISTORY_CAP`. */
  appendHistory: (entry: HistoryEntryInput) => void;
}

/** Unique enough for a per-session log: timestamp plus an in-session counter. */
let counter = 0;
function nextId(): string {
  counter += 1;
  return `h${Date.now().toString(36)}-${counter}`;
}

/** Build a history store; storage is injectable by tests. */
export function createHistoryStore(deps: { storage?: TypedStorage } = {}) {
  const store = deps.storage ?? appStorage;
  return create<HistoryState>((set, get) => ({
    entries: migrateHistoryEntries(store.get<unknown>(HISTORY_KEY, [])),
    appendHistory: (entry) => {
      const full: HistoryEntry = {
        ...entry,
        id: nextId(),
        timestamp: Date.now(),
      };
      const next = [full, ...get().entries].slice(0, HISTORY_CAP);
      store.set(HISTORY_KEY, next);
      set({ entries: next });
    },
  }));
}

export const useHistoryStore = createHistoryStore();
